import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Menu, X, Home, LayoutGrid, Tags, Star, Mail, Sparkles } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { EdgeFinancesLogo } from '../common/EdgeFinancesLogo';

type LinkItem = {
  icon: typeof Home;
  text: string;
  href: string;
};

const linkItems: LinkItem[] = [
  { icon: Home, text: 'Overview', href: '#home' },
  { icon: Sparkles, text: 'Features', href: '#features' },
  { icon: LayoutGrid, text: 'How it works', href: '#flow' },
  { icon: Tags, text: 'Pricing', href: '#pricing' },
  { icon: Star, text: 'Customers', href: '#stories' },
  { icon: Mail, text: 'Contact', href: '#contact' },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 8);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const mobileDrawer =
    typeof document !== 'undefined'
      ? createPortal(
          <>
            <div
              className={`fixed inset-0 z-[200] bg-black/70 transition-opacity duration-200 ease-out lg:hidden ${
                isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
              }`}
              onClick={() => setIsOpen(false)}
              aria-hidden={!isOpen}
            />
            <aside
              className={`fixed bottom-0 right-0 top-0 z-[210] flex w-[min(88vw,20rem)] flex-col bg-black pt-[var(--safe-area-inset-top,env(safe-area-inset-top,0px))] pb-[var(--safe-area-inset-bottom,env(safe-area-inset-bottom,0px))] shadow-[-24px_0_64px_rgba(0,0,0,0.85)] transition-transform duration-200 ease-out lg:hidden ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`}
              aria-hidden={!isOpen}
            >
              <div className="flex h-[4.25rem] shrink-0 items-center justify-between border-b border-zinc-800 bg-black px-5">
                <EdgeFinancesLogo variant="onDark" size="sm" showWordmark />
                <button
                  type="button"
                  className="rounded-lg border border-zinc-700 bg-zinc-900 p-2 text-zinc-100"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close menu"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex flex-1 flex-col gap-1 overflow-y-auto bg-black px-3 py-4">
                {linkItems.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    className="flex items-center gap-3 rounded-xl bg-black px-4 py-3.5 text-[15px] font-medium text-zinc-100 hover:bg-zinc-900"
                    onClick={() => setIsOpen(false)}
                  >
                    <item.icon className="h-5 w-5 shrink-0 text-emerald-400" />
                    {item.text}
                  </a>
                ))}
              </div>

              <div className="shrink-0 space-y-2 border-t border-zinc-800 bg-black px-4 py-4 pb-[max(1rem,var(--safe-area-inset-bottom,env(safe-area-inset-bottom,0px)))]">
                <NavLink
                  to="/login"
                  className="block rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-center font-semibold text-white"
                  onClick={() => setIsOpen(false)}
                >
                  Sign in
                </NavLink>
                <NavLink
                  to="/register?plan=pro"
                  className="block rounded-xl bg-gradient-to-b from-emerald-500 to-emerald-700 py-3 text-center font-semibold text-white shadow-lg"
                  onClick={() => setIsOpen(false)}
                >
                  Start free
                </NavLink>
              </div>
            </aside>
          </>,
          document.body,
        )
      : null;

  const linkClass =
    'group relative flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-medium text-slate-400 transition-colors hover:text-white';

  const navShell = scrolled
    ? 'border-white/[0.12] bg-zinc-950/80 shadow-[0_12px_48px_-12px_rgba(0,0,0,0.55)] backdrop-blur-2xl backdrop-saturate-150'
    : 'border-white/[0.08] bg-zinc-950/45 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-2xl backdrop-saturate-150';

  return (
    <nav
      className={`fixed top-0 z-50 w-full border-b pt-[var(--safe-area-inset-top,env(safe-area-inset-top,0px))] font-sans transition-all duration-500 ${navShell}`}
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/[0.04] to-transparent" aria-hidden />

      <div className="relative container mx-auto px-4">
        <div className="flex h-[4.25rem] items-center justify-between gap-4">
          <NavLink to="/" className="group flex min-w-0 shrink-0 items-center">
            <EdgeFinancesLogo variant="onDark" size="md" showWordmark className="opacity-95 transition-opacity group-hover:opacity-100" />
          </NavLink>

          <div className="hidden items-center gap-0.5 rounded-2xl border border-white/[0.06] bg-black/20 px-1 py-1 lg:flex">
            {linkItems.map((item) => (
              <a key={item.href} href={item.href} className={linkClass}>
                <item.icon className="h-3.5 w-3.5 text-slate-500 transition-colors group-hover:text-emerald-400" />
                {item.text}
              </a>
            ))}
          </div>

          <div className="hidden items-center gap-2 sm:flex">
            <NavLink
              to="/login"
              className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2 text-sm font-semibold text-slate-200 transition-colors hover:border-white/20 hover:bg-white/[0.08] hover:text-white"
            >
              Sign in
            </NavLink>
            <NavLink
              to="/register?plan=pro"
              className="rounded-xl bg-gradient-to-b from-emerald-500 to-emerald-700 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-950/35 ring-1 ring-emerald-400/20 transition-all hover:from-emerald-400 hover:to-emerald-600"
            >
              Start free
            </NavLink>
          </div>

          <button
            className="rounded-xl border border-white/10 bg-white/[0.05] p-2.5 text-slate-200 transition-colors hover:bg-white/[0.1] lg:hidden"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileDrawer}
    </nav>
  );
}
